import { Box, BoxProps } from '@air/zephyr';
import { useReducedMotion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

export interface VideoProps extends BoxProps<'video'> {
  details: {
    height: number;
    width: number;
  };
  sources: {
    src: string;
    type: string;
  }[];
}

export const Video = ({
  autoPlay = true,
  details,
  loop = true,
  muted = true,
  playsInline = true,
  preload = 'none',
  poster,
  sources,
  tx,
  ...restOfProps
}: VideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [ref, inView] = useInView();
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    if (shouldReduceMotion) return;

    if (inView && !hasLoadedOnce) {
      videoRef?.current?.load();
      setHasLoadedOnce(true);
    } else if (inView) {
      videoRef?.current?.play();
    } else if (hasLoadedOnce && loop) {
      videoRef?.current?.pause();
    }
  }, [hasLoadedOnce, inView, loop, shouldReduceMotion, videoRef]);

  return (
    <Box
      tx={{ position: 'relative', ...tx }}
      style={{
        paddingBottom: details && details.height && details.width && `${(details.height / details.width) * 100}%`,
      }}
      ref={ref}
      {...restOfProps}
    >
      <Box
        as="video"
        autoPlay={autoPlay}
        playsInline={playsInline}
        loop={loop}
        /**
         * Key is required be unique so react knows that this is an entirely new component
         * @see https://stackoverflow.com/a/47382850
         */
        key={sources?.map((source) => source?.src).join('-')}
        muted={muted}
        poster={inView ? poster : ''}
        preload={preload}
        ref={videoRef}
        tx={{
          /**
           * This is to prevent the video from have a stretching on initial load.
           * @see https://stackoverflow.com/a/39668112
           */
          objectFit: 'fill',
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
        }}
      >
        {sources?.map((source) => {
          return (
            <Box
              as="source"
              key={`${source.type}-${source.src}`}
              src={inView ? source.src : undefined}
              type={source.type}
            />
          );
        })}
      </Box>
    </Box>
  );
};
